"use client";
import React, { useState } from "react";
import { usePopper } from "react-popper";

interface PopperCords {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface UseMapViewOverlaysPopperReturn {
  selectedResourceCoords: PopperCords;
  setSelectedResourceCoords: React.Dispatch<React.SetStateAction<PopperCords>>;
  highlightResourceCoords: PopperCords;
  setHighlightResourceCoords: React.Dispatch<React.SetStateAction<PopperCords>>;

  referenceDetailsHighlightElement: Element | null;
  setReferenceDetailsHighlightElement: React.Dispatch<
    React.SetStateAction<Element | null>
  >;
  popperDetailsHighlightElement: HTMLElement | null;
  setPopperDetailsHighlightElement: React.Dispatch<
    React.SetStateAction<HTMLElement | null>
  >;
  stylesDetailsHighlight: { [key in string]: React.CSSProperties };
  attributesDetailsHighlight: {
    [p: string]: { [p: string]: string } | undefined;
  };

  referenceResDetailsElement: Element | null;
  setReferenceResDetailsElement: React.Dispatch<
    React.SetStateAction<Element | null>
  >;
  popperResDetailsElement: HTMLElement | null;
  setPopperResDetailsElement: React.Dispatch<
    React.SetStateAction<HTMLElement | null>
  >;
  stylesResDetails: { [key in string]: React.CSSProperties };
  attributesResDetails: { [p: string]: { [p: string]: string } | undefined };

  forceUpdateResDetails?: (() => void) | null;
  updateResDetails?: (() => Promise<Partial<unknown>>) | null;
}

export const useMapViewOverlaysPopper = (
  isMapBuilderMap = false
): UseMapViewOverlaysPopperReturn => {
  const [selectedResourceCoords, setSelectedResourceCoords] =
    React.useState<PopperCords>({
      x: 0,
      y: 0,
      width: 0,
      height: 0
    });

  const [highlightResourceCoords, setHighlightResourceCoords] =
    React.useState<PopperCords>({
      x: 0,
      y: 0,
      width: 0,
      height: 0
    });

  const virtualSelectedResourceReference = React.useRef({
    getBoundingClientRect: () => ({
      width: 0,
      height: 0,
      top: selectedResourceCoords.y,
      left: selectedResourceCoords.x,
      right: selectedResourceCoords.x,
      bottom: selectedResourceCoords.y,
      x: selectedResourceCoords.x,
      y: selectedResourceCoords.y,
      toJSON: () => {} // Prevent serialization errors
    })
  });

  virtualSelectedResourceReference.current.getBoundingClientRect = () => ({
    width: 0,
    height: 0,
    top: selectedResourceCoords.y,
    left: selectedResourceCoords.x,
    right: selectedResourceCoords.x,
    bottom: selectedResourceCoords.y,
    x: selectedResourceCoords.x,
    y: selectedResourceCoords.y,
    toJSON: () => {}
  });

  const virtualHighlightResourceReference = React.useRef({
    getBoundingClientRect: () => ({
      width: 0,
      height: 0,
      top: highlightResourceCoords.y,
      left: highlightResourceCoords.x,
      right: highlightResourceCoords.x,
      bottom: highlightResourceCoords.y,
      x: highlightResourceCoords.x,
      y: highlightResourceCoords.y,
      toJSON: () => {} // Prevent serialization errors
    })
  });

  virtualHighlightResourceReference.current.getBoundingClientRect = () => ({
    width: 0,
    height: 0,
    top: highlightResourceCoords.y,
    left: highlightResourceCoords.x,
    right: highlightResourceCoords.x,
    bottom: highlightResourceCoords.y,
    x: highlightResourceCoords.x,
    y: highlightResourceCoords.y,
    toJSON: () => {}
  });

  // Popper styling instructions for resource details hover
  const [
    referenceDetailsHighlightElement,
    setReferenceDetailsHighlightElement
  ] = useState<Element | null>(null);
  const [popperDetailsHighlightElement, setPopperDetailsHighlightElement] =
    useState<HTMLElement | null>(null);
  const {
    styles: stylesDetailsHighlight,
    attributes: attributesDetailsHighlight
  } = usePopper(
    isMapBuilderMap
      ? virtualHighlightResourceReference.current
      : referenceDetailsHighlightElement,
    popperDetailsHighlightElement,
    {
      placement: "auto",
      strategy: "fixed",
      modifiers: [
        {
          name: "offset",
          enabled: true,
          options: {
            offset: [10, 10]
          }
        }
      ]
    }
  );

  // Popper styling instructions for resource details "click"
  const [referenceResDetailsElement, setReferenceResDetailsElement] =
    useState<Element | null>(null);
  const [popperResDetailsElement, setPopperResDetailsElement] =
    useState<HTMLElement | null>(null);
  const {
    styles: stylesResDetails,
    attributes: attributesResDetails,
    forceUpdate: forceUpdateResDetails,
    update: updateResDetails
  } = usePopper(
    isMapBuilderMap
      ? virtualSelectedResourceReference.current
      : referenceResDetailsElement,
    popperResDetailsElement,
    {
      placement: "auto",
      modifiers: [
        {
          name: "offset",
          enabled: true,
          options: {
            offset: [10, 10]
          }
        }
      ]
    }
  );

  return {
    selectedResourceCoords,
    setSelectedResourceCoords,
    highlightResourceCoords,
    setHighlightResourceCoords,

    referenceDetailsHighlightElement,
    setReferenceDetailsHighlightElement,
    popperDetailsHighlightElement,
    setPopperDetailsHighlightElement,
    stylesDetailsHighlight,
    attributesDetailsHighlight,

    referenceResDetailsElement,
    setReferenceResDetailsElement,
    popperResDetailsElement,
    setPopperResDetailsElement,
    stylesResDetails,
    attributesResDetails,

    forceUpdateResDetails,
    updateResDetails
  };
};
